import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorMessage, gebruiktMockApi, http } from './api'
import {
  mockMaakSemester,
  mockSemesterDetail,
  mockSemesters,
  mockVerwijderSemester,
  mockWijzigSemester,
  mockWijzigSleutels,
} from './__fixtures__/beheer-fixtures'
import { vertraag } from './__fixtures__/vertraag'
import type {
  NieuwSemester,
  Semester,
  SemesterDetail,
  SemesterWijziging,
  Sleutels,
} from './semester-types'

export interface SemesterApi {
  list(): Promise<Semester[]>
  get(semesterId: string): Promise<SemesterDetail>
  maak(input: NieuwSemester): Promise<SemesterDetail>
  wijzig(semesterId: string, input: SemesterWijziging): Promise<SemesterDetail>
  verwijder(semesterId: string): Promise<void>
  wijzigSleutels(semesterId: string, input: Partial<Sleutels>): Promise<Sleutels>
}

class RealSemesterApi implements SemesterApi {
  list(): Promise<Semester[]> {
    return http.get<Semester[]>('/api/semesters')
  }
  get(semesterId: string): Promise<SemesterDetail> {
    return http.get<SemesterDetail>(`/api/semesters/${semesterId}`)
  }
  maak(input: NieuwSemester): Promise<SemesterDetail> {
    return http.post<SemesterDetail>('/api/semesters', input)
  }
  wijzig(semesterId: string, input: SemesterWijziging): Promise<SemesterDetail> {
    return http.patch<SemesterDetail>(`/api/semesters/${semesterId}`, input)
  }
  verwijder(semesterId: string): Promise<void> {
    return http.del<void>(`/api/semesters/${semesterId}`)
  }
  wijzigSleutels(semesterId: string, input: Partial<Sleutels>): Promise<Sleutels> {
    return http.patch<Sleutels>(`/api/semesters/${semesterId}/sleutels`, input)
  }
}

class MockSemesterApi implements SemesterApi {
  list(): Promise<Semester[]> {
    return vertraag(mockSemesters())
  }
  get(semesterId: string): Promise<SemesterDetail> {
    return vertraag(mockSemesterDetail(semesterId))
  }
  maak(input: NieuwSemester): Promise<SemesterDetail> {
    return vertraag(mockMaakSemester(input), 160)
  }
  wijzig(semesterId: string, input: SemesterWijziging): Promise<SemesterDetail> {
    return vertraag(mockWijzigSemester(semesterId, input), 120)
  }
  verwijder(semesterId: string): Promise<void> {
    mockVerwijderSemester(semesterId)
    return vertraag(undefined, 120)
  }
  wijzigSleutels(semesterId: string, input: Partial<Sleutels>): Promise<Sleutels> {
    return vertraag(mockWijzigSleutels(semesterId, input), 120)
  }
}

export const semesterApi: SemesterApi = gebruiktMockApi
  ? new MockSemesterApi()
  : new RealSemesterApi()

export const semesterKeys = {
  alle: ['semesters'] as const,
  lijst: () => ['semesters', 'lijst'] as const,
  detail: (semesterId: string) => ['semesters', 'detail', semesterId] as const,
}

export function useSemesters() {
  return useQuery({
    queryKey: semesterKeys.lijst(),
    queryFn: () => semesterApi.list(),
    staleTime: 300_000,
  })
}

export function useSemesterDetail(semesterId: string) {
  return useQuery({
    queryKey: semesterKeys.detail(semesterId),
    queryFn: () => semesterApi.get(semesterId),
    enabled: semesterId !== '',
    staleTime: 300_000,
  })
}

export function useSleutels(semesterId: string): Sleutels | null {
  return useSemesterDetail(semesterId).data?.sleutels ?? null
}

export function useMaakSemester() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NieuwSemester) => semesterApi.maak(input),
    onSuccess: (semester) => {
      queryClient.invalidateQueries({ queryKey: semesterKeys.alle })
      toast.success(`${semester.naam} aangemaakt`)
    },
    onError: (error) => toast.error(errorMessage(error, 'Aanmaken mislukt')),
  })
}

export function useWijzigSemester() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { semesterId: string; input: SemesterWijziging }) =>
      semesterApi.wijzig(vars.semesterId, vars.input),
    onSuccess: (semester) => {
      queryClient.setQueryData(semesterKeys.detail(semester.id), semester)
      queryClient.invalidateQueries({ queryKey: semesterKeys.alle })
      toast.success('Semester bijgewerkt')
    },
    onError: (error) => toast.error(errorMessage(error, 'Bijwerken mislukt')),
  })
}

export function useVerwijderSemester() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (semesterId: string) => semesterApi.verwijder(semesterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: semesterKeys.alle })
      toast.success('Semester verwijderd')
    },
    onError: (error) => toast.error(errorMessage(error, 'Verwijderen mislukt')),
  })
}

export function useWijzigSleutels(semesterId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<Sleutels>) => semesterApi.wijzigSleutels(semesterId, input),
    onSuccess: (sleutels) => {
      queryClient.setQueryData(
        semesterKeys.detail(semesterId),
        (huidig: SemesterDetail | undefined) => (huidig ? { ...huidig, sleutels } : huidig),
      )
      queryClient.invalidateQueries()
      toast.success('Sleutels opgeslagen')
    },
    onError: (error) => toast.error(errorMessage(error, 'Opslaan mislukt')),
  })
}
